import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { withTranslation } from 'react-i18next';

import { SlateDialog } from 'components/Shared/SlateDialog';
import SlateTextInput from 'components/Shared/SlateTextInput';
import WizardSteps from 'components/WizardSteps/WizardSteps';
import p from 'theme/palette';

var STEP_CREATE = 1;
var STEP_COMPLETE = 2;

var MaterialSymbol = function(props) {
  var name = props.name;
  var className = props.className || '';
  return (
    <span
      className={'material-symbols-outlined ' + className}
      style={{ fontSize: 20, lineHeight: 1 }}
    >
      {name}
    </span>
  );
};

var useStyles = makeStyles(function() {
  return {
    wizardContainer: {
      padding: '8px 0 0',
    },
    stepContent: {
      padding: '16px 0 0',
    },
    description: {
      fontSize: 13,
      lineHeight: 1.6,
      color: p.grey600,
      margin: '0 0 20px',
    },
    inputContainer: {
      margin: 0,
    },
    // Complete step - matching ShareModal pattern
    keyName: {
      fontSize: 14,
      fontWeight: 600,
      color: p.defaultText,
      marginBottom: 16,
    },
    urlContainer: {
      display: 'flex',
      alignItems: 'stretch',
      gap: 0,
      marginBottom: 16,
    },
    urlField: {
      flex: 1,
      padding: '12px 14px',
      backgroundColor: p.grey200,
      borderRadius: '8px 0 0 8px',
      border: '1px solid ' + p.border,
      borderRight: 'none',
      fontFamily: "'SF Mono', 'Menlo', 'Monaco', 'Consolas', monospace",
      fontSize: 12,
      color: p.defaultText,
      lineHeight: 1.5,
      wordBreak: 'break-all',
      userSelect: 'all',
      cursor: 'text',
      transition: 'border-color 0.15s ease, background-color 0.15s ease',
      '&:hover': {
        borderColor: p.grey400,
      },
      '&:focus': {
        outline: 'none',
        borderColor: p.primary,
        backgroundColor: p.white,
      },
    },
    copyButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 16px',
      backgroundColor: p.primary,
      border: 'none',
      borderRadius: '0 8px 8px 0',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease',
      '&:hover': {
        backgroundColor: p.primaryH,
      },
      '&:active': {
        backgroundColor: '#0f3d73',
      },
    },
    copyButtonSuccess: {
      backgroundColor: p.success,
      '&:hover': {
        backgroundColor: '#047857',
      },
      '&:active': {
        backgroundColor: '#065f46',
      },
    },
    copyIcon: {
      fontSize: 20,
      color: p.white,
    },
    warning: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
      padding: '12px 14px',
      backgroundColor: p.infoWarningBg,
      borderRadius: 6,
    },
    warningIcon: {
      color: p.infoWarningColor,
      fontSize: 18,
      flexShrink: 0,
      marginTop: 1,
    },
    warningText: {
      fontSize: 13,
      lineHeight: 1.5,
      color: p.infoWarningColor,
      margin: 0,
    },
  };
});

var ApiKeyWizard = function(props) {
  var open = props.open;
  var onClose = props.onClose;
  var onSubmit = props.onSubmit;
  var isFetching = props.isFetching;
  var createdApiKey = props.createdApiKey;
  var t = props.t;

  var classes = useStyles();

  var nameState = useState('');
  var name = nameState[0];
  var setName = nameState[1];

  var errorState = useState('');
  var error = errorState[0];
  var setError = errorState[1];

  var stepState = useState(STEP_CREATE);
  var currentStep = stepState[0];
  var setCurrentStep = stepState[1];

  var copiedState = useState(false);
  var copied = copiedState[0];
  var setCopied = copiedState[1];

  // Reset state when dialog opens
  useEffect(function() {
    if (open) {
      setName('');
      setError('');
      setCurrentStep(STEP_CREATE);
      setCopied(false);
    }
  }, [open, setCopied, setCurrentStep, setError, setName]);

  // Move to complete step when key is created
  useEffect(function() {
    if (createdApiKey && currentStep === STEP_CREATE) {
      setCurrentStep(STEP_COMPLETE);
    }
  }, [createdApiKey, currentStep, setCurrentStep]);

  var handleNameChange = useCallback(function(e) {
    setName(e.target.value);
    if (error) {
      setError('');
    }
  }, [error, setError, setName]);

  var handleSubmit = useCallback(function() {
    var trimmedName = name.trim();
    if (!trimmedName) {
      setError(t('validation:required'));
      return;
    }
    onSubmit({ name: trimmedName });
  }, [name, onSubmit, t, setError]);

  var handleKeyPress = useCallback(function(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  var handleCopy = useCallback(function() {
    if (createdApiKey && createdApiKey.apiKey) {
      copy(createdApiKey.apiKey);
      setCopied(true);
      setTimeout(function() {
        setCopied(false);
      }, 2000);
    }
  }, [createdApiKey, setCopied]);

  var handleKeyClick = useCallback(function(e) {
    var selection = window.getSelection();
    var range = document.createRange();
    range.selectNodeContents(e.target);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  var handleClose = useCallback(function() {
    if (!isFetching) {
      onClose();
    }
  }, [isFetching, onClose]);

  var getTitle = function() {
    if (currentStep === STEP_COMPLETE) {
      return t('stackSettings:apiKeyCreated');
    }
    return t('stackSettings:createApiKey');
  };

  var getActions = function() {
    if (currentStep === STEP_CREATE) {
      return [
        {
          label: t('btn:cancel'),
          onClick: handleClose,
          variant: 'secondary',
          disabled: isFetching,
          key: 'cancel',
        },
        {
          label: t('btn:create'),
          onClick: handleSubmit,
          variant: 'primary',
          disabled: isFetching || !name.trim(),
          loading: isFetching,
          key: 'create',
        },
      ];
    }
    return [
      {
        label: t('btn:done'),
        onClick: handleClose,
        variant: 'primary',
        key: 'done',
      },
    ];
  };

  var renderCreateStep = function() {
    return (
      <div className={classes.stepContent}>
        <p className={classes.description}>
          {t('stackSettings:createApiKeyDescription')}
        </p>
        <div className={classes.inputContainer}>
          <SlateTextInput
            name="apiKeyName"
            type="text"
            label={t('stackSettings:apiKeyName')}
            placeholder={t('stackSettings:apiKeyNamePlaceholder')}
            fullWidth
            value={name}
            onChange={handleNameChange}
            onKeyPress={handleKeyPress}
            error={error}
            disabled={isFetching}
            autoFocus
          />
        </div>
      </div>
    );
  };

  var renderCompleteStep = function() {
    if (!createdApiKey) return null;

    var copyBtnClass = classes.copyButton;
    if (copied) copyBtnClass += ' ' + classes.copyButtonSuccess;

    return (
      <div className={classes.stepContent}>
        <p className={classes.description}>
          {t('stackSettings:apiKeyCreatedDescription')}
        </p>

        <div className={classes.keyName}>{createdApiKey.name}</div>

        <div className={classes.urlContainer}>
          <div
            className={classes.urlField}
            onClick={handleKeyClick}
            role="textbox"
            tabIndex={0}
            aria-readonly="true"
            aria-label="API Key"
          >
            {createdApiKey.apiKey}
          </div>
          <button
            type="button"
            className={copyBtnClass}
            onClick={handleCopy}
            aria-label={copied ? 'Copied' : 'Copy API key'}
          >
            <MaterialSymbol
              name={copied ? 'check' : 'content_copy'}
              className={classes.copyIcon}
            />
          </button>
        </div>

        <div className={classes.warning}>
          <MaterialSymbol name="warning" className={classes.warningIcon} />
          <p className={classes.warningText}>
            {t('stackSettings:storeKey')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <SlateDialog
      open={open}
      onClose={handleClose}
      title={getTitle()}
      closable={!isFetching}
      actions={getActions()}
    >
      <div className={classes.wizardContainer}>
        <WizardSteps steps={[1, 2]} currentStep={currentStep} />
        {currentStep === STEP_CREATE && renderCreateStep()}
        {currentStep === STEP_COMPLETE && renderCompleteStep()}
      </div>
    </SlateDialog>
  );
};

ApiKeyWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  createdApiKey: PropTypes.shape({
    name: PropTypes.string,
    apiKey: PropTypes.string,
    uuid: PropTypes.string,
  }),
  t: PropTypes.func.isRequired,
};

ApiKeyWizard.defaultProps = {
  isFetching: false,
  createdApiKey: null,
};

export default withTranslation()(ApiKeyWizard);
